/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { useSessionState } from "./session.js";

import {
  AbsoluteTime,
  AccessToken,
  AmountJson,
  Amounts,
  OperationOk,
  Result,
  TalerBankConversionErrorsByMethod,
  TalerBankConversionHttpClient,
  TalerBankConversionResultByMethod,
  TalerCoreBankErrorsByMethod,
  TalerCoreBankResultByMethod,
  TalerCorebankApi,
  TalerHttpError,
  TokenAuth,
  assertUnreachable,
  opFixedSuccess,
} from "@gnu-taler/taler-util";
import { dummyHttpResponse } from "@gnu-taler/taler-util/http";
import {
  buildPaginatedResult,
  useBankCoreApiContext,
  useListPointer,
} from "@gnu-taler/web-util/browser";
import _useSWR, { SWRHook, mutate } from "swr";
import { PAGINATED_LIST_REQUEST } from "../utils.js";

// FIX default import https://github.com/microsoft/TypeScript/issues/49189
const useSWR = _useSWR as unknown as SWRHook;

export type TransCalc = {
  debit: AmountJson;
  credit: AmountJson;
  beforeFee: AmountJson;
};
export type TransferCalculation = TransCalc | undefined;
type EstimatorFunction = (
  amount: AmountJson,
  fee: AmountJson,
) => Promise<
  | OperationOk<TransCalc>
  | TalerBankConversionErrorsByMethod<"getCashinRate">
  | TalerBankConversionErrorsByMethod<"getCashoutRate">
>;

type ConversionEstimators = {
  estimateByCredit: EstimatorFunction;
  estimateByDebit: EstimatorFunction;
};

export function revalidateConversionInfo() {
  return mutate(
    (key) =>
      Array.isArray(key) && key[key.length - 1] === "getConversionInfoAPI",
  );
}
export function useConversionInfo() {
  const {
    lib: { conversion },
    config,
  } = useBankCoreApiContext();

  async function fetcher() {
    return await conversion.getConfig();
  }
  const { data, error } = useSWR<
    TalerBankConversionResultByMethod<"getConfig">,
    TalerHttpError
  >(!config.allow_conversion ? undefined : ["getConversionInfoAPI"], fetcher, {
    refreshInterval: 0,
    refreshWhenHidden: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    errorRetryCount: 0,
    errorRetryInterval: 1,
    shouldRetryOnError: false,
    keepPreviousData: true,
  });

  if (data) return data;
  if (error) return error;
  return undefined;
}

export function useConversionRateForUser(
  username: string,
  token: AccessToken | undefined,
) {
  const {
    lib: { conversionForUser },
    config,
  } = useBankCoreApiContext();

  async function fetcher([usr, tkn]: [string, AccessToken | undefined]) {
    return await conversionForUser(usr).getRate(
      tkn != null ? { type: "bearer", token: tkn } : undefined,
    );
  }
  const { data, error } = useSWR<
    TalerBankConversionResultByMethod<"getRate">,
    TalerHttpError
  >(
    !config.allow_conversion
      ? undefined
      : [username, token, "useConversionInfoForUser"],
    fetcher,
    {
      refreshInterval: 0,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      errorRetryCount: 0,
      errorRetryInterval: 1,
      shouldRetryOnError: false,
      keepPreviousData: true,
    },
  );

  if (data) return data;
  if (error) return error;
  return undefined;
}

export function revalidateConversionRateForUser() {
  return mutate(
    (key) =>
      Array.isArray(key) && key[key.length - 1] === "useConversionInfoForUser",
    undefined,
    { revalidate: true },
  );
}

function buildEstimatorWithTheBackend(
  conversion: TalerBankConversionHttpClient,
  token: AccessToken | undefined,
  estimation:
    | "cashin-rate-from-credit"
    | "cashin-rate-from-debit"
    | "cashout-rate-from-credit"
    | "cashout-rate-from-debit",
): EstimatorFunction {
  return async (amount, fee) => {
    const auth: TokenAuth | undefined =
      token != null ? { type: "bearer", token } : undefined;
    let resp;
    switch (estimation) {
      case "cashin-rate-from-credit": {
        resp = await conversion.getCashinRate(auth, {
          credit: amount,
        });
        break;
      }
      case "cashin-rate-from-debit": {
        resp = await conversion.getCashinRate(auth, {
          debit: amount,
        });
        break;
      }
      case "cashout-rate-from-credit": {
        resp = await conversion.getCashoutRate(auth, {
          credit: amount,
        });
        break;
      }
      case "cashout-rate-from-debit": {
        resp = await conversion.getCashoutRate(auth, {
          debit: amount,
        });
        break;
      }
      default: {
        assertUnreachable(estimation);
      }
    }
    if (resp.type === "fail") {
      return resp;
    }
    const credit = Amounts.parseOrThrow(resp.body.amount_credit);
    const debit = Amounts.parseOrThrow(resp.body.amount_debit);
    const beforeFee = Amounts.add(credit, fee).amount;

    return opFixedSuccess(dummyHttpResponse, {
      debit,
      beforeFee,
      credit,
    });
  };
}

function useConversionEstimatorsWithTheBackend(
  conversion: TalerBankConversionHttpClient,
  direction: "cashin" | "cashout",
): ConversionEstimators {
  const { state } = useSessionState();
  const token = state.status === "loggedIn" ? state.token : undefined;
  return {
    estimateByCredit: buildEstimatorWithTheBackend(
      conversion,
      token,
      direction == "cashin"
        ? "cashin-rate-from-credit"
        : "cashout-rate-from-credit",
    ),
    estimateByDebit: buildEstimatorWithTheBackend(
      conversion,
      token,
      direction == "cashin"
        ? "cashin-rate-from-debit"
        : "cashout-rate-from-debit",
    ),
  };
}

export function useCashinEstimator(): ConversionEstimators {
  const {
    lib: { conversion },
  } = useBankCoreApiContext();

  return useConversionEstimatorsWithTheBackend(conversion, "cashin");
}

export function useCashoutEstimator(): ConversionEstimators {
  const {
    lib: { conversion },
  } = useBankCoreApiContext();
  return useConversionEstimatorsWithTheBackend(conversion, "cashout");
}

export function useCashinEstimatorForClass(
  classId: number,
): ConversionEstimators {
  const {
    lib: { conversionForClass },
  } = useBankCoreApiContext();
  return useConversionEstimatorsWithTheBackend(
    conversionForClass(classId),
    "cashin",
  );
}

export function useCashoutEstimatorForClass(
  classId: number,
): ConversionEstimators {
  const {
    lib: { conversionForClass },
  } = useBankCoreApiContext();
  return useConversionEstimatorsWithTheBackend(
    conversionForClass(classId),
    "cashout",
  );
}

export function useCashoutEstimatorByUser(
  username: string,
): ConversionEstimators {
  const {
    lib: { conversionForUser },
  } = useBankCoreApiContext();
  return useConversionEstimatorsWithTheBackend(
    conversionForUser(username),
    "cashout",
  );
}

export async function revalidateBusinessAccounts() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "listAccounts",
    undefined,
    { revalidate: true },
  );
}
function accountRowId(account: TalerCorebankApi.AccountMinimalData): string {
  return String(account.row_id ?? 0);
}

export function useBusinessAccounts(filterName?: string) {
  const { state: credentials } = useSessionState();
  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  const [pointer, setPointer] =
    useListPointer<TalerCorebankApi.AccountMinimalData>(
      accountRowId,
      filterName,
    );

  function fetcher([token, offset, order, account]: [
    AccessToken,
    string | undefined,
    "asc" | "dec",
    string | undefined,
  ]) {
    return api.listAccounts(token, {
      limit: PAGINATED_LIST_REQUEST,
      offset,
      order,
      account,
    });
  }

  const { data, error } = useSWR<
    TalerCoreBankResultByMethod<"listAccounts">,
    TalerHttpError
  >([token, pointer.id, pointer.order, filterName, "listAccounts"], fetcher, {
    refreshInterval: 0,
    refreshWhenHidden: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    errorRetryCount: 0,
    errorRetryInterval: 1,
    shouldRetryOnError: false,
    keepPreviousData: true,
  });

  if (error) return Result.error(error);
  if (data === undefined) return undefined;
  if (data.type !== "ok") return Result.error(data);

  //TODO: row_id should not be optional
  return Result.of(
    buildPaginatedResult(
      data.body.accounts,
      pointer,
      setPointer,
      PAGINATED_LIST_REQUEST,
    ),
  );
}

type CashoutWithId = TalerCorebankApi.CashoutStatusResponse & { id: number };
export interface CashoutDetailFailure {
  id: number;
  case: number;
}

async function mapWithConcurrency<Input, Output>(
  input: readonly Input[],
  concurrency: number,
  mapper: (item: Input) => Promise<Output>,
): Promise<Output[]> {
  const output = new Array<Output>(input.length);
  let next = 0;
  async function worker(): Promise<void> {
    while (next < input.length) {
      const index = next++;
      output[index] = await mapper(input[index]);
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(concurrency, input.length) }, () => worker()),
  );
  return output;
}
export function revalidateCashouts() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "useCashouts",
  );
}
export function useCashouts(account: string) {
  const { state: credentials } = useSessionState();
  const {
    lib: { bank: api },
    config,
  } = useBankCoreApiContext();
  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;

  async function fetcher([username, token]: [string, AccessToken]) {
    const list = await api.getAccountCashouts({ username, token });
    if (list.type !== "ok") {
      return list;
    }
    const all = await mapWithConcurrency(
      list.body.cashouts,
      4,
      async (
        c,
      ): Promise<
        | { type: "ok"; cashout: CashoutWithId }
        | { type: "fail"; failure: CashoutDetailFailure }
      > => {
        const r = await api.getCashoutById({ username, token }, c.cashout_id);
        if (r.type === "fail") {
          return {
            type: "fail",
            failure: { id: c.cashout_id, case: r.case as number },
          };
        }
        return {
          type: "ok",
          cashout: { ...r.body, id: c.cashout_id },
        };
      },
    );
    return opFixedSuccess(dummyHttpResponse, {
      cashouts: all.flatMap((result) =>
        result.type === "ok" ? [result.cashout] : [],
      ),
      failures: all.flatMap((result) =>
        result.type === "fail" ? [result.failure] : [],
      ),
    });
  }
  const { data, error } = useSWR<
    | OperationOk<{
        cashouts: CashoutWithId[];
        failures: CashoutDetailFailure[];
      }>
    | TalerCoreBankErrorsByMethod<"getAccountCashouts">,
    TalerHttpError
  >(
    !config.allow_conversion ? undefined : [account, token, "useCashouts"],
    fetcher,
    {
      refreshInterval: 0,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      errorRetryCount: 0,
      errorRetryInterval: 1,
      shouldRetryOnError: false,
      keepPreviousData: true,
    },
  );

  if (data) return data;
  if (error) return error;
  return undefined;
}

export function revalidateCashoutDetails() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "getCashoutById",
    undefined,
    { revalidate: true },
  );
}
export function useCashoutDetails(cashoutId: number | undefined) {
  const { state: credentials } = useSessionState();
  const creds = credentials.status !== "loggedIn" ? undefined : credentials;
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  async function fetcher([username, token, id]: [string, AccessToken, number]) {
    return api.getCashoutById({ username, token }, id);
  }

  const { data, error } = useSWR<
    TalerCoreBankResultByMethod<"getCashoutById">,
    TalerHttpError
  >(
    cashoutId === undefined
      ? undefined
      : [creds?.username, creds?.token, cashoutId, "getCashoutById"],
    fetcher,
    {
      refreshInterval: 0,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      errorRetryCount: 0,
      errorRetryInterval: 1,
      shouldRetryOnError: false,
      keepPreviousData: true,
    },
  );

  if (data) return data;
  if (error) return error;
  return undefined;
}
export type LastMonitor = {
  current: TalerCoreBankResultByMethod<"getMonitor">;
  previous: TalerCoreBankResultByMethod<"getMonitor">;
};
export function revalidateLastMonitorInfo() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "useLastMonitorInfo",
    undefined,
    { revalidate: true },
  );
}
export function useLastMonitorInfo(
  currentMoment: AbsoluteTime,
  previousMoment: AbsoluteTime,
  timeframe: TalerCorebankApi.MonitorTimeframeParam,
) {
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();
  const { state: credentials } = useSessionState();

  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;

  async function fetcher([token, timeframe]: [
    AccessToken,
    TalerCorebankApi.MonitorTimeframeParam,
  ]) {
    const [current, previous] = await Promise.all([
      api.getMonitor(token, { timeframe, date: currentMoment }),
      api.getMonitor(token, { timeframe, date: previousMoment }),
    ]);
    return {
      current,
      previous,
    };
  }

  const { data, error } = useSWR<LastMonitor, TalerHttpError>(
    !token ? undefined : [token, timeframe, "useLastMonitorInfo"],
    fetcher,
    {
      refreshInterval: 0,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      errorRetryCount: 0,
      errorRetryInterval: 1,
      shouldRetryOnError: false,
      keepPreviousData: true,
    },
  );

  if (data) return data;
  if (error) return error;
  return undefined;
}

export function revalidateConversionRateClasses() {
  return mutate(
    (key) =>
      Array.isArray(key) && key[key.length - 1] === "useConversionRateClasses",
    undefined,
    { revalidate: true },
  );
}
export function useConversionRateClasses() {
  const { state: credentials } = useSessionState();
  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  const [pointer, setPointer] = useListPointer(
    (row: TalerCorebankApi.ConversionRateClass) =>
      String(row.conversion_rate_class_id),
    undefined,
    { displayOrder: "asc" },
  );

  function fetcher([token, offset, order]: [
    AccessToken,
    string | undefined,
    "asc" | "dec",
  ]) {
    return api.listConversionRateClasses(token, {
      limit: PAGINATED_LIST_REQUEST,
      offset,
      order,
    });
  }

  const { data, error } = useSWR<
    TalerCoreBankResultByMethod<"listConversionRateClasses">,
    TalerHttpError
  >([token, pointer.id, pointer.order, "useConversionRateClasses"], fetcher, {
    refreshInterval: 0,
    refreshWhenHidden: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    errorRetryCount: 0,
    errorRetryInterval: 1,
    shouldRetryOnError: false,
    keepPreviousData: true,
  });

  if (error) return Result.error(error);
  if (data === undefined) return undefined;
  if (data.type !== "ok") return Result.error(data);

  return Result.of(
    buildPaginatedResult(
      data.body.classes,
      pointer,
      setPointer,
      PAGINATED_LIST_REQUEST,
    ),
  );
}

export function revalidateConversionRateClassDetails() {
  return mutate(
    (key) =>
      Array.isArray(key) &&
      key[key.length - 1] === "useConversionRateClassDetails",
    undefined,
    { revalidate: true },
  );
}

export function useConversionRateClassDetails(classId: number) {
  const { state: credentials } = useSessionState();
  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  async function fetcher([username, token]: [number, AccessToken]) {
    return await api.getConversionRateClass(token, username);
  }

  const { data, error } = useSWR<
    TalerCoreBankResultByMethod<"getConversionRateClass">,
    TalerHttpError
  >([classId, token, "useConversionRateClassDetails"], fetcher, {});

  if (data) return data;
  if (error) return error;
  return undefined;
}

export function revalidateConversionRateClassUsers() {
  return mutate(
    (key) =>
      Array.isArray(key) &&
      key[key.length - 1] === "useConversionRateClassUsers",
    undefined,
    { revalidate: true },
  );
}

export function useConversionRateClassUsers(
  classId: number | undefined,
  username?: string,
) {
  const { state: credentials } = useSessionState();
  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  const [pointer, setPointer] = useListPointer(
    (row: TalerCorebankApi.AccountMinimalData) => String(row.row_id ?? 0),
    `${classId ?? "none"}:${username ?? ""}`,
    { displayOrder: "asc" },
  );

  function fetcher([token, offset, order, username, classId]: [
    AccessToken,
    string | undefined,
    "asc" | "dec",
    string,
    number,
  ]) {
    return api.listAccounts(token, {
      limit: PAGINATED_LIST_REQUEST,
      offset,
      order,
      account: username,
      conversionRateId: classId,
    });
  }

  const { data, error } = useSWR<
    TalerCoreBankResultByMethod<"listAccounts">,
    TalerHttpError
  >(
    [
      token,
      pointer.id,
      pointer.order,
      username,
      classId,
      "useConversionRateClassUsers",
    ],
    fetcher,
    {
      refreshInterval: 0,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      errorRetryCount: 0,
      errorRetryInterval: 1,
      shouldRetryOnError: false,
      keepPreviousData: true,
    },
  );

  if (error) return Result.error(error);
  if (data === undefined) return undefined;
  if (data.type !== "ok") return Result.error(data);

  return Result.of(
    buildPaginatedResult(
      data.body.accounts,
      pointer,
      setPointer,
      PAGINATED_LIST_REQUEST,
    ),
  );
}
